import * as React from "react";
import {
  AccessReportParametersModel,
} from "@app/access-reports/wizard";
import { FlyoutLink } from "@app/shared/Flyouts";
import { ColumnConfig, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { FC, JSX, useEffect, useRef, useState } from "react";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons"; 
import { QESRadioButton } from "@app/shared/qes-standards/forms/RadioButton";
import { AccessReportsWizardAccessCriteria } from "@app/access-reports/shared/accessReportsWizardAccessCriteria";
import { AccessReportsWizardTemplate } from "@app/access-reports/shared/accessReportsWizardTemplate";
import { buildStandard } from "@app/access-reports/shared/utility";
import { AccessReportsWizardEditNetworks } from "@app/access-reports/wizard/AccessReportsWizardEditNetworks";

interface AccessReportsWizardStepAccessAnalysisProps {
  accessAnalysisWizardForm: React.ReactNode;
  accessReportParameters: AccessReportParametersModel;
  accessReportsWizardTemplate: AccessReportsWizardTemplate;
  onAccessCriteriaCheckboxToggle: (selectedKeys: string[], selectedGroupKey: string) => void;
  onGroupedAccessCriterionClick: (groupKey: string, selectedKeys: string[]) => void;
  onProductChange: (accessCriteria: AccessReportsWizardAccessCriteria) => void;
}

interface AccessAnalysisTableData {
  [wafflesKeyColumnName]: string;
  edit: JSX.Element;
  standards: string;
  type: string;
}

const AccessReportsWizardStepAccessAnalysis: FC<AccessReportsWizardStepAccessAnalysisProps> = (props: AccessReportsWizardStepAccessAnalysisProps) => {
  const getColumnConfig = (): ColumnConfig => {
    return ({
      edit: {
        displayText: "Edit",
        hidden: !props.accessReportParameters.product.editable,
        noSort: true,
        testId: "edit",
      },
      standards: {
        displayText: "Standards",
        noSort: true,
        testId: "standards",
      },
      type: {
        displayText: "Type",
        noSort: true,
        testId: "type"
      },
    });
  }

  const ungroupedKey = useRef("Ungrouped");
  const tablesColumnConfig = useRef<ColumnConfig>(getColumnConfig());

  const [groupedAccessCriterion, setGroupedAccessCriterion] = useState(new Map<string, AccessReportsWizardAccessCriteria[]>());
  const [selectedAccessCriterion, setSelectedAccessCriterion] = useState<AccessReportsWizardAccessCriteria[]>([]);
  const [selectedGroupKey, setSelectedGroupKey] = useState<string>(null);
  const [sidePanelVisible, setSidePanelVisible] = useState(false);

  useEffect(() => {
    if (!!props.accessReportParameters.product?.accessCriterion) {
      buildGroupedAccessCriterion();
    }
  }, [props.accessReportParameters.product.accessCriterion]);

  const buildAccessCriterionTables = (): React.ReactNode => {
    return (
      <React.Fragment>
        {(groupedAccessCriterion.keys().next().value !== ungroupedKey.current && groupedAccessCriterion.size !== 1) &&
          <div className="row">
            <div className="col-12">
              <label className="label-m mb-3">
                Please select one of the following groups:
              </label>
            </div>
          </div>
        }

        {Array.from(groupedAccessCriterion.keys()).sort((a, b) => (getSortValue(a, b))).map((groupKey, index) => (
          <React.Fragment key={index}>
            {groupKey === ungroupedKey.current ?
              <div className="row">
                <div className="col-12">
                  <label className="label-s mb-3">
                    Select ungrouped access criterion:
                  </label>
                </div>
              </div> :
              <div className="row">
                <div className="col-12">
                  <QESRadioButton
                    label={groupKey}
                    labelClass="label-s"
                    radioContainerClassName="ms-n3"
                    checked={selectedGroupKey === groupKey}
                    className="qes-radio"
                    testId={`access-reports-wizard__access-analysis--grouped-access-criteria-${index}`}
                    onChange={() => onGroupedAccessCriterionClick(groupKey)}
                    name="access-analysis-groups"
                  />
                </div>
              </div>
            }

            <div className="row">
              <div className="col-12">
                <div className="ms-5">
                  {buildAccessCriterionTable(groupKey, index)}
                </div>
              </div>
            </div>
          </React.Fragment>
        ))}
      </React.Fragment>
    );
  }

  const buildAccessCriterionTable = (groupKey: string, groupIndex: number): React.ReactNode => {
    const accessCriterion: AccessReportsWizardAccessCriteria[] = groupedAccessCriterion.get(groupKey);
    const filteredAccessCriterion: AccessReportsWizardAccessCriteria[] = accessCriterion && accessCriterion.filter((x) => !x.hidden);

    if (!!filteredAccessCriterion && filteredAccessCriterion.length) {
      const data: AccessAnalysisTableData[] = filteredAccessCriterion.map((accessCriteria, index) => ({
        /* oxlint-disable sort-keys */
        [wafflesKeyColumnName]: accessCriteria.id.toString(),
        type: accessCriteria.name,
        standards: buildStandard(accessCriteria, props.accessReportParameters),
        edit: (
          <FlyoutLink
            classNames="qes-button-secondary"
            content={{
              body: renderEditNetworkSidePanel(accessCriteria) as JSX.Element,
              title: "Edit Network",
            }}
            panelId={`access-reports-wizard__access-analysis--access-criteria-group-${groupIndex}-edit-${index}`}
            isVisible={sidePanelVisible}
            size="40"
            testId={`access-reports-wizard__access-analysis--access-criteria-group-${groupIndex}-edit-${index}-link`}
          >
            <QesIcon iconName={QesIcons.PencilV1} description="Edit" className="text-info" />
          </FlyoutLink>
        ),
        /* oxlint-enable */
      }));

      return (
        <div className="access-repotrs-wizard-access-criterion-table">
          <Waffles<AccessAnalysisTableData>
            columns={tablesColumnConfig.current}
            data={data}
            disableProcessingOverlay={true}
            handleCheckboxChange={handleCheckboxChange}
            fullWidth={true}
            selectedKeys={selectedAccessCriterion.map((ac) => ac.id.toString())}
            showCheckboxes={groupKey === ungroupedKey.current ? true : false}
            testId={`access-reports-wizard-access-analysis-${groupKey}`}
          />
        </div>
      );
    }
  }

  const buildGroupedAccessCriterion = (): void => {
    const newGroupedAccessCriterion: Map<string, AccessReportsWizardAccessCriteria[]> = new Map<string, AccessReportsWizardAccessCriteria[]>();

    if (props.accessReportParameters && props.accessReportParameters.product && props.accessReportParameters.standard && props.accessReportParameters.calculationMethod) {
      for (const accessCriteria of props.accessReportParameters.product.accessCriterion) {
        if (accessCriteria.groups && accessCriteria.groups.length > 0) {
          // grouped access criterion
          for (const group of accessCriteria.groups) {
            const existingMapping: AccessReportsWizardAccessCriteria[] = newGroupedAccessCriterion.get(group);

            if (existingMapping) {
              // if there is an existing mapping for this group key,
              // add to it, rather than overwriting it
              if (!existingMapping.includes(accessCriteria)) {
                existingMapping.push(accessCriteria);
                newGroupedAccessCriterion.set(group, existingMapping);
              }
            } else {
              newGroupedAccessCriterion.set(group, [accessCriteria]);
            }
          }
        } else {
          // ungrouped access criterion
          const existingMapping: AccessReportsWizardAccessCriteria[] = newGroupedAccessCriterion.get(ungroupedKey.current);

          if (existingMapping) {
            // if there is an existing mapping for this group key,
            // add to it, rather than overwriting it
            if (!existingMapping.includes(accessCriteria)) {
              existingMapping.push(accessCriteria);
              newGroupedAccessCriterion.set(ungroupedKey.current, existingMapping);
            }
          } else {
            newGroupedAccessCriterion.set(ungroupedKey.current, [accessCriteria]);
          }
        }
      }
    }

    const newSelectedGroupKey: string = getSelectedGroupKey(newGroupedAccessCriterion);
    // only include previously-selected access criterion if ungrouped 
    const newSelectedAccessCriterion: AccessReportsWizardAccessCriteria[] = props.accessReportParameters.product.accessCriterion.filter((x) => x.selected && (!x.groups || !x.groups.length));

    setGroupedAccessCriterion(newGroupedAccessCriterion);
    setSelectedAccessCriterion(newSelectedAccessCriterion);
    setSelectedGroupKey(newSelectedGroupKey);
  }

  const getSelectedGroupKey = (groupedAccessCriterion: Map<string, AccessReportsWizardAccessCriteria[]>): string => {
    let selectedGroupKey = "";
    let selectedGroupKeyLength = 0;

    if (groupedAccessCriterion.size) {
      for (const [groupKey, accessCriterion] of groupedAccessCriterion) {
        const selectedAccessCriterion: AccessReportsWizardAccessCriteria[] = accessCriterion.filter((x) => x.selected);

        if (selectedAccessCriterion.length === accessCriterion.length) {
          if (!selectedGroupKey) {
            selectedGroupKey = groupKey;
          } else {
            if (selectedAccessCriterion.length > selectedGroupKeyLength) {
              selectedGroupKey = groupKey;
            }
          }

          selectedGroupKeyLength = accessCriterion.length;
        }
      }
    }

    return selectedGroupKey;
  }

  const getSortValue = (a: string, b: string) => {
    if (a === ungroupedKey.current) {
      // ungrouped access criterion should sort after everything else
      return 1;
    } else if (b === ungroupedKey.current) {
      // ungrouped access criterion should sort after everything else
      return -1;
    } else {
      // sort by a group key's number (if it has one), then alphabetically
      // first, split the group key by whitespace,
      // also completely removing any whitespace
      // to avoid selecting it in the next step
      const splitGroupKeyA: string[] = a.split(/(\s+)/).filter((x) => {
        return x.trim().length > 0;
      });
      const splitGroupKeyB: string[] = b.split(/(\s+)/).filter((x) => {
        return x.trim().length > 0;
      });

      // next, select the first split that is a number
      const numberA: string = splitGroupKeyA.find(x => !isNaN(Number(x)));
      const numberB: string = splitGroupKeyB.find(x => !isNaN(Number(x)));

      if (numberA && numberB) {
        // comparing two numbers
        return a < b ? -1 : 1;
      } else if (numberA) {
        // a is a number
        return 1;
      } else if (numberB) {
        // b is a number
        return -1;
      } else {
        // comparing two letters
        return a < b ? -1 : 1;
      }
    }
  }

  const handleCheckboxChange = (selectedKeys: string[]): void => {
    const newSelectedAccessCriterion: AccessReportsWizardAccessCriteria[] =
      props.accessReportParameters.product.accessCriterion.filter((x) => selectedKeys.includes(x.id.toString()));

    props.onAccessCriteriaCheckboxToggle(
      selectedKeys,
      selectedGroupKey,
    );

    setSelectedAccessCriterion(newSelectedAccessCriterion);
  }

  const onGroupedAccessCriterionClick = (groupKey: string): void => {
    props.onGroupedAccessCriterionClick(
      groupKey,
      selectedAccessCriterion.map((ac: AccessReportsWizardAccessCriteria) => ac.id.toString()),
    );

    setSelectedGroupKey(groupKey);
  }

  const onProductChange = (accessCriteria: AccessReportsWizardAccessCriteria): void => {
    props.onProductChange(accessCriteria);

    if (sidePanelVisible) {
      setSidePanelVisible(false);
    }
  }

  const renderEditNetworkSidePanel = (accessCriteria: AccessReportsWizardAccessCriteria): React.ReactNode => {
    return (
      <AccessReportsWizardEditNetworks
        accessCriteria={accessCriteria}
        accessReportParameters={props.accessReportParameters}
        onProductChange={onProductChange}
        product={props.accessReportParameters.product}
      />
    );
  }

  return (
    <React.Fragment>
      <div className="row">
        <div className="col-12">
          <h4 className="header-s mb-3">
            Product: {(props.accessReportParameters && props.accessReportParameters.product) && props.accessReportParameters.product.name}
          </h4>
        </div>
      </div>

      {props.accessAnalysisWizardForm}

      <div className="row">
        <div className="col-12">
          <hr className="mt0" />
        </div>
      </div>

      {(!!props.accessReportParameters.standard && !!props.accessReportParameters.calculationMethod) ?
        buildAccessCriterionTables() :
        <div className="row">
          <div className="col-12">
            <p className="text-center copy-text-l">
              Please select an access standard and a calculation method.
            </p>
          </div>
        </div>
      }
    </React.Fragment>
  );
}

export { AccessReportsWizardStepAccessAnalysis };
